import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductsGrid = ({ loading, products, addingToCart, handleAddToCart, getCategoryLabel }) => {
    if (loading) {
        return (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div key={i} className="bg-surface rounded-xl h-96 animate-pulse" />
                ))}
            </div>
        );
    }
    if (products.length === 0) {
        return (
            <div className="text-center py-16">
                <p className="text-muted text-lg">Nessun prodotto trovato</p>
            </div>
        );
    }
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
                <Link
                    key={product.id}
                    to={`/store/${product.id}`}
                    className="product-card group"
                    data-testid={`store-product-${product.id}`}
                >
                    <div className="aspect-square bg-surface-elevated overflow-hidden">
                        <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                    </div>
                    <div className="p-4">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-xs text-primary font-medium uppercase tracking-wider">
                                {getCategoryLabel(product.category)}
                            </span>
                            {product.brand && (
                                <span className="text-xs text-muted">{product.brand}</span>
                            )}
                        </div>
                        <h3 className="font-semibold line-clamp-2 mb-2">{product.name}</h3>
                        <p className="text-sm text-muted line-clamp-2 mb-4">{product.description}</p>
                        <div className="flex items-center justify-between">
                            <p className="text-2xl font-bold text-primary">
                                €{product.price.toFixed(2)}
                            </p>
                            <Button
                                size="sm"
                                className="bg-primary hover:bg-primary-hover"
                                onClick={(e) => handleAddToCart(e, product.id)}
                                disabled={addingToCart === product.id || product.stock === 0}
                                data-testid={`add-to-cart-${product.id}`}
                            >
                                {addingToCart === product.id ? (
                                    <span className="animate-spin">...</span>
                                ) : (
                                    <ShoppingCart className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                        {product.stock === 0 && (
                            <p className="text-danger text-sm mt-2">Non disponibile</p>
                        )}
                        {product.stock > 0 && product.stock < 5 && (
                            <p className="text-warning text-sm mt-2">Solo {product.stock} disponibili</p>
                        )}
                    </div>
                </Link>
            ))}
        </div>
    );
};

const StorePage = () => {
    const { user } = useAuth();
    const { addToCart } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [addingToCart, setAddingToCart] = useState(null);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const params = category !== 'all' ? { category } : {};
                const response = await axios.get(`${API}/api/products`, { params });
                setProducts(response.data);
            } catch {
                toast.error('Errore nel caricamento dei prodotti');
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [category]);

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(search.toLowerCase()) ||
        product.description.toLowerCase().includes(search.toLowerCase())
    );

    const handleAddToCart = async (e, productId) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!user) {
            toast.error('Effettua il login per aggiungere al carrello');
            return;
        }
        
        setAddingToCart(productId);
        try {
            await addToCart(productId, 1);
            toast.success('Prodotto aggiunto al carrello');
        } catch {
            toast.error('Errore nell\'aggiunta al carrello');
        } finally {
            setAddingToCart(null);
        }
    };

    const getCategoryLabel = (cat) => {
        const labels = {
            new: 'Nuovo',
            refurbished: 'Ricondizionato',
            accessory: 'Accessorio',
            spare_part: 'Ricambio'
        };
        return labels[cat] || cat;
    };

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-2">Store</h1>
                    <p className="text-muted">Dispositivi ricondizionati, accessori e pezzi di ricambio</p>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
                        <Input
                            placeholder="Cerca prodotti..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 bg-surface border-border"
                            data-testid="search-input"
                        />
                    </div>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-full sm:w-48 bg-surface border-border" data-testid="category-filter">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Categoria" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-border">
                            <SelectItem value="all">Tutte le categorie</SelectItem>
                            <SelectItem value="new">Nuovi</SelectItem>
                            <SelectItem value="refurbished">Ricondizionati</SelectItem>
                            <SelectItem value="accessory">Accessori</SelectItem>
                            <SelectItem value="spare_part">Pezzi di ricambio</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Products Grid */}
                <ProductsGrid loading={loading} products={filteredProducts} addingToCart={addingToCart} handleAddToCart={handleAddToCart} getCategoryLabel={getCategoryLabel} />
            </div>
        </div>
    );
};

export default StorePage;
